// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import cloneDeep from "lodash.clonedeep";

import {
  ActionType,
  IAction,
  IActionChangeChargerSettings,
  IActionChargingPower,
  IActionNewChargerStatusNotification,
  IActionNewChargingSessionData,
  IActionNewChargerData,
  IActionOcppCharger,
  IActionOcppConnector,
  IActionOcppTrendInfo,
  IActionTypeChargingSessionList,
  IActionTypeRfidList,
} from "actions";
import { ConnectorStatus } from "model/connector";
import {
  IConnectorData,
  INITIAL_STATE,
  IRefusedRfid,
  IStateOcpp,
  ITariff,
} from "states/ocppstates";
import { ChargingMode } from "dbtypes";
import { isSet } from "utils";
import { ChargerControl } from "graphql/api";
import { parseApiInt } from "apiutils";
import { IState } from "states/states";

function parseNewChargerData(
  connectorState: (IConnectorData | undefined)[],
  data: IActionNewChargerData,
) {
  // ensure existing object
  if (!isSet(connectorState[data.connector])) {
    connectorState[data.connector] = {};
  }
  if (isSet(data.timestamp)) {
    connectorState[data.connector]!.lastUpdate = data.timestamp;
  }
  if (isSet(data.power)) {
    connectorState[data.connector]!.power = data.power;
  }
  if (isSet(data.current)) {
    connectorState[data.connector]!.current = data.current;
  }
  if (isSet(data.voltage)) {
    connectorState[data.connector]!.voltage = data.voltage;
  }
  if (isSet(data.sessionEnergy)) {
    connectorState[data.connector]!.sessionEnergy = data.sessionEnergy;
  }
  if (isSet(data.energyMeter)) {
    connectorState[data.connector]!.energymeter = data.energyMeter;
  }
}

function parseNewChargerStatus(
  connectorState: (IConnectorData | undefined)[],
  data: IActionNewChargerStatusNotification,
) {
  // ensure existing object
  if (!isSet(connectorState[data.connector])) {
    connectorState[data.connector] = {};
  }
  if (isSet(data.timestamp)) {
    connectorState[data.connector]!.statusTimestamp = data.timestamp;
  }

  // update in connector status?
  if (isSet(data.statusCode)) {
    connectorState[data.connector]!.status = data.statusCode;
    // we also reset the power values to zero, to have an immediate update and not just
    // after the next MeterValues message, which may take some time (i.e. for go-e)
    // also refer to ocppbackend/statusnotification
    if (
      [
        ConnectorStatus.SUSPENDED_EV,
        ConnectorStatus.SUSPENDED_EVSE,
        ConnectorStatus.AVAILABLE,
        ConnectorStatus.FINISHING,
      ].includes(data.statusCode)
    ) {
      // if we just got a finish/suspended state, we also clear current power values
      connectorState[data.connector]!.power = 0;
      connectorState[data.connector]!.current = [0, 0, 0];
    }
  }
  if (isSet(data.errorCode)) {
    connectorState[data.connector]!.errorCode = data.errorCode;
  }
}

export default function ocppReducer(
  state: IStateOcpp,
  action: IAction<unknown>,
  rootState: IState,
): IStateOcpp {
  // type convert action string back into ActionType
  switch (action.type) {
    case ActionType.ACT_DISCONNECT_SITE:
    case ActionType.ACT_LOGOUT_SUCC:
      state = cloneDeep(INITIAL_STATE);
      return state;

    case ActionType.ACT_GET_OCPP_CHARGERS_SUCC: {
      const payload = action.payload as IActionOcppCharger[];
      // we cannot just replace all chargers as we have connector data structures based on the same index
      // we update the structure but we do not remove obsolete chargers so we cannot delete them!
      // also, we just remove inactive chargers and do not show them at all
      payload
        .filter((curCharger) => curCharger.isActive)
        .forEach((curCharger) => {
          // extract additional payload fields
          const { lastMessage, ...newCharger } = curCharger;
          // find existing charger entry
          let chargerIndex = state.charger.findIndex(
            (curItem) => curItem.fk_charger === newCharger.fk_charger,
          );
          if (chargerIndex >= 0) {
            // replace existing charger
            state.charger[chargerIndex] = newCharger;
          } else {
            // append new charger
            chargerIndex = state.charger.length;
            state.charger.push(newCharger);
            state.connectorData[chargerIndex] = [];
          }
          // save last message
          if (lastMessage) {
            state.lastOcppMessage[chargerIndex] = lastMessage;
          }
        });
      return state;
    }

    case ActionType.ACT_GET_OCPP_CONNECTORS_SUCC: {
      const payload = action.payload as IActionOcppConnector[];
      payload.forEach((curData) => {
        const chargerIndex = state.charger.findIndex(
          (curCharger) => curCharger.fk_charger === curData.fk_charger,
        );
        if (chargerIndex >= 0) {
          const newConnectorConfig = {
            name: curData.name,
            evseid: curData.evseid,
            plugType: curData.plugType,
            nominalPower: curData.nominalPower,
            isDc: curData.isDc,
            nrPhases: curData.nrPhases,
          };
          // ensure existing connector element
          if (state.connectorConfig[chargerIndex] === undefined)
            state.connectorConfig[chargerIndex] = [];
          state.connectorConfig[chargerIndex][curData.connector] = newConnectorConfig;

          // do the same with connectorData
          // however, do not replace whole object as it may contain other data (e.g. reqMaxPower)
          if (state.connectorData[chargerIndex] === undefined)
            state.connectorData[chargerIndex] = [];
          if (state.connectorData[chargerIndex][curData.connector] === undefined)
            state.connectorData[chargerIndex][curData.connector] = {};
          const connectorData = state.connectorData[chargerIndex][curData.connector]!;
          // fill data
          // some data might not be available in curData and we might overwrite/delete existing data then
          // we accept this as this is executed only once at connection and the deleted data can
          // only be from enline mqtt data, which we will get again soon
          connectorData.lastUpdate = curData.lastUpdate;
          connectorData.power = curData.power;
          connectorData.current = curData.current;
          connectorData.voltage = curData.voltage;
          connectorData.sessionEnergy = curData.sessionEnergy;
          connectorData.energymeter = curData.energymeter;
          connectorData.status = curData.connectorStatus;
          connectorData.statusTimestamp = curData.connectorStatusTimestamp;
        } else {
          // this might happen when a charger was deleted
          console.warn("connector data for non-monitored charger", curData);
        }
      });
      return state;
    }

    case ActionType.ACT_SAVE_OCPP_CHARGERS_SUCC: {
      const payload = action.payload as IActionChangeChargerSettings;
      // we only support changing ocpp chargers, so fk_charger is given
      const chargerIndex = state.charger.findIndex((curItem) => curItem.fk_charger === payload.id);
      if (chargerIndex >= 0) {
        state.charger[chargerIndex].name = payload.name;
        state.charger[chargerIndex].nominalPower = payload.nominalPower;
        state.charger[chargerIndex].restrictAuth = payload.restrictAuth ?? false;
        state.charger[chargerIndex].coordinates = payload.coordinates;

        // also update connectors config
        if (!state.connectorConfig[chargerIndex]) {
          console.error("creating new connector config", chargerIndex, state.charger);
          state.connectorConfig[chargerIndex] = [];
        }
        for (let index = 0; index < (payload.connector?.length ?? 0); index++) {
          const connectorReq = payload.connector![index];
          if (connectorReq) {
            state.connectorConfig[chargerIndex][index] = connectorReq;
          }
        }
      } else {
        console.error("unknown charger", payload.id, state.charger);
      }
      return state;
    }

    case ActionType.ACT_GET_OCPP_RFIDS_BASIC_SUCC:
      state.rfid = action.payload as IActionTypeRfidList;
      return state;
    case ActionType.ACT_GET_OCPP_RFIDS_EXT_SUCC:
      state.rfid = action.payload as IActionTypeRfidList;
      return state;

    case ActionType.ACT_GET_OCPP_REFUSED_RFIDS_SUCC:
      state.refusedRfid = action.payload as IRefusedRfid[];
      return state;

    case ActionType.ACT_GET_OCPP_SITE_TARIFFS_SUCC:
      state.siteTariff = action.payload as ITariff[];
      return state;
    case ActionType.ACT_GET_OCPP_SESSION_TARIFFS_SUCC:
      state.sessionTariff = action.payload as ITariff[];
      return state;

    case ActionType.ACT_GET_OCPP_TRENDINFO_SUCC: {
      const payload = action.payload as IActionOcppTrendInfo;
      state.minSessionTimestamp = payload.minTrendTimestamp;
      state.maxSessionTimestamp = payload.maxTrendTimestamp;
      return state;
    }

    case ActionType.ACT_GET_OCPP_SESSIONS_SUCC: {
      const payload = action.payload as IActionTypeChargingSessionList;
      if (rootState.site.user?.isRfidUser) {
        // take all sessions, just as received
        state.chargingSession = payload;
      } else {
        // remove sessions from chargers not shown on site
        const chargerIds = state.charger.map((curItem) => curItem.fk_charger);
        state.chargingSession = payload.filter((curItem) =>
          chargerIds.includes(curItem.fk_charger),
        );
      }
      return state;
    }

    case ActionType.ACT_GET_PENDING_OCPP_SESSIONS_SUCC:
      state.pendingChargingSession = action.payload as IActionTypeChargingSessionList;
      return state;

    case ActionType.ACT_NEW_OCPP_DATA: {
      const payload = action.payload as IActionNewChargerData;
      const chargerIndex = state.charger.findIndex(
        (curItem) => curItem.fk_charger === payload.chargerId,
      );
      if (chargerIndex < 0) {
        // this is OK; we might have received a message from a virtual site of a non-selected charger
        return state;
      }
      state.lastOcppMessage[chargerIndex] = payload.timestamp;
      // ensure existance
      if (!Array.isArray(state.connectorData[chargerIndex])) {
        state.connectorData[chargerIndex] = [];
      }
      parseNewChargerData(state.connectorData[chargerIndex], payload);
      return state;
    }

    case ActionType.ACT_NEW_CHARGER_STATUS: {
      const payload = action.payload as IActionNewChargerStatusNotification;
      const chargerIndex = state.charger.findIndex(
        (curItem) => curItem.fk_charger === payload.chargerId,
      );
      if (chargerIndex < 0) {
        // this is OK; we might have received a message from a virtual site of a non-selected charger
        return state;
      }
      state.lastOcppMessage[chargerIndex] = payload.timestamp;
      // ensure existance
      if (!Array.isArray(state.connectorData[chargerIndex])) {
        state.connectorData[chargerIndex] = [];
      }
      parseNewChargerStatus(state.connectorData[chargerIndex], payload);
      return state;
    }

    case ActionType.ACT_NEW_CHARGING_SESSION_DATA: {
      const payload = action.payload as IActionNewChargingSessionData;
      // is this a new pending session?
      if (!payload.stopDate) {
        // start of session
        state.pendingChargingSession.push({
          id: payload.id,
          fk_charger: payload.chargerId,
          connector: payload.connector,
          rfid: payload.rfid,
          rfidId: payload.rfidId,
          startTimestamp: payload.startDate!,
          startMeter: payload.startMeter!,
          fk_tariff: payload.tariffId,
        });
        // put this in state.chargingSession?
        // we should, but we do not easily know whether the filter fitted...
      } else {
        // end of session; remove from pending list
        const index = state.pendingChargingSession.findIndex(
          (curItem) => curItem.id === payload.id,
        );
        if (index >= 0) {
          state.pendingChargingSession.splice(index, 1);
        } else {
          console.warn("update inexisting pending session", payload, state.pendingChargingSession);
        }
        // update entry if it exists in session list
        state.chargingSession
          .filter((curItem) => curItem.id === payload.id)
          .forEach((curItem) => {
            curItem.stopTimestamp = payload.stopDate;
            curItem.stopMeter = payload.stopMeter;
            curItem.stopReason = payload.stopReason;
            curItem.costs = payload.costs;
          });
      }
      return state;
    }

    case ActionType.ACT_SELECT_CHARGING_SESSION:
      state.chargingControl = undefined;
      return state;

    case ActionType.ACT_GET_CHARGER_POWER_SUCC: {
      const payload = action.payload as ChargerControl | null;
      // convert id field from string to number
      state.chargingControl = payload
        ? {
            id: parseApiInt(payload.id),
            isActive: payload.isActive,
            mode: payload.mode as string as ChargingMode,
            maxPower: payload.maxPower ?? undefined,
            targetEnergy: payload.targetEnergy ?? undefined,
            targetTimestamp: payload.targetTimestamp ?? undefined,
          }
        : undefined;
      return state;
    }
    case ActionType.ACT_SAVE_CHARGER_POWER_SUCC: {
      const payload = action.payload as IActionChargingPower;
      state.chargingControl = {
        id: state.chargingControl?.id ?? Date.now(),
        isActive: payload.isActive,
        mode: payload.mode,
        maxPower: payload.maxPower,
        targetEnergy: payload.targetEnergy,
        targetTimestamp: payload.targetTimestamp,
      };
      return state;
    }

    default:
      return state;
  }
}
